import { z } from "zod";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";
import { bytesFromBinaryString } from "./binary";

export const MAX_PRINT_PROFILE_BYTES = 4 * 1024 * 1024;
const fail = (): never => {
  throw new Error("PRINT_PROFILE_INVALID");
};
/** Bounded structural inspection; the renderer's color engine validates the actual transform. */
export function inspectPrintProfile(bytes: Uint8Array) {
  if (bytes.length < 144 || bytes.length > MAX_PRINT_PROFILE_BYTES)
    return fail();
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength),
    signature = (offset: number) =>
      String.fromCharCode(...bytes.subarray(offset, offset + 4)),
    tags = view.getUint32(128);
  if (
    view.getUint32(0) !== bytes.length ||
    signature(36) !== "acsp" ||
    signature(12) !== "prtr" ||
    signature(16) !== "CMYK" ||
    !["XYZ ", "Lab "].includes(signature(20)) ||
    ![2, 4].includes(bytes[8]) ||
    tags < 1 ||
    tags > 512 ||
    132 + tags * 12 > bytes.length
  )
    return fail();
  const names = new Set<string>();
  for (let i = 0; i < tags; i++) {
    const index = 132 + i * 12,
      offset = view.getUint32(index + 4),
      length = view.getUint32(index + 8),
      name = signature(index);
    if (
      names.has(name) ||
      offset % 4 ||
      offset < 132 + tags * 12 ||
      length < 8 ||
      offset + length > bytes.length
    )
      return fail();
    names.add(name);
  }
  return {
    bytes: bytes.length,
    version: bytes[8],
    colorSpace: "CMYK" as const,
    deviceClass: "prtr" as const,
  };
}
function encode(bytes: Uint8Array) {
  const chunks: string[] = [];
  for (let offset = 0; offset < bytes.length; offset += 8192)
    chunks.push(String.fromCharCode(...bytes.subarray(offset, offset + 8192)));
  return btoa(chunks.join(""));
}
export function printProfileBytes(profile: { data: string; sha256: string }) {
  if (
    typeof profile.data !== "string" ||
    profile.data.length > Math.ceil(MAX_PRINT_PROFILE_BYTES / 3) * 4 ||
    profile.data.length % 4 ||
    !/^[A-Za-z0-9+/]*={0,2}$/.test(profile.data)
  )
    return fail();
  let binary: string;
  try {
    binary = atob(profile.data);
  } catch {
    return fail();
  }
  if (btoa(binary) !== profile.data) return fail();
  const bytes = bytesFromBinaryString(binary);
  inspectPrintProfile(bytes);
  if (bytesToHex(sha256(bytes)) !== profile.sha256) return fail();
  return bytes;
}
export const printProfileSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1)
      .max(120)
      .refine((value) => !/[\u0000-\u001f\u007f]/.test(value)),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    data: z.string().max(Math.ceil(MAX_PRINT_PROFILE_BYTES / 3) * 4),
  })
  .strict()
  .superRefine((profile, context) => {
    try {
      printProfileBytes(profile);
    } catch {
      context.addIssue({ code: "custom", message: "PRINT_PROFILE_INVALID" });
    }
  });
export type PrintProfile = z.infer<typeof printProfileSchema>;
export function createPrintProfile(
  bytes: Uint8Array,
  name: string,
): PrintProfile {
  inspectPrintProfile(bytes);
  return printProfileSchema.parse({
    name,
    data: encode(bytes),
    sha256: bytesToHex(sha256(bytes)),
  });
}
